import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { TAGS, DIFFICULTY } from '../../util/constants';
import { usePremium } from '../../util/PremiumFilter';
import { cleanProfanity } from '../../util/filterProfanity';
import axios from 'axios';

const GameTitle = ({ gameMetadata, onPlayerInfoClick, playerPanelsExpanded = false }) => {
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [showDifficultyTooltip, setShowDifficultyTooltip] = useState(false);
  const [creatorData, setCreatorData] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const { renderUserWithBadge, PREMIUM_TIERS } = usePremium();
  // Color scheme constants (matching Stockfish)
  const colors = {
    primary: '#1a2f16',
    secondary: '#2a4020',
    tertiary: 'sandybrown',
    background: '#1a2635',
  };

  // Extract creator handle with fallback to ensure consistency
  const creatorHandle = gameMetadata?.creatorHandle ||
    (gameMetadata?.creator && typeof gameMetadata.creator === 'string' && !gameMetadata.creator.includes(' ') ?
      gameMetadata.creator : 'Anonymous');

  // Fetch creator's data including premium status
  useEffect(() => {
    const fetchCreatorData = async () => {
      if (creatorHandle && creatorHandle !== 'Anonymous') {
        try {
          const response = await axios.get(`/user/${creatorHandle}`);
          if (response.data && response.data.user) {
            setCreatorData(response.data.user);
          }
        } catch (error) {
          console.error(`Error fetching creator data: ${error}`);
        }
      }
    };
    
    fetchCreatorData();
  }, [creatorHandle]);

  // Function to handle tag click and redirect to portal with tag filter
  const handleTagClick = (tag) => {
    // Find the label for this tag to match what's displayed in the Portal tabs
    const tagLabel = TAGS[tag]?.label || tag;

    // When redirecting to portal, also update the current URL to include the tag
    // This ensures that if the user returns to this game, the tag context is preserved
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tag', tag);

    // Update the current URL without navigating away
    window.history.replaceState(
      {},
      '',
      `${currentPath}?${searchParams.toString()}`
    );

    // Now navigate to the portal with the tag
    history.push('/portal', { activeTag: tagLabel });
  };

  // Get difficulty from game metadata with fallback to "All Levels"
  const difficulty = gameMetadata?.difficulty || "All Levels";

  // Get the first letter of the difficulty for the icon
  const difficultyLetter = difficulty.charAt(0);

  // Get appropriate difficulty icon based on difficulty level
  const getDifficultyIcon = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'all levels':
        return <i className="fa-solid fa-chess-board"></i>;
      case 'beginner':
        return <i className="fa-solid fa-chess-pawn"></i>;
      case 'intermediate':
        return <i className="fa-solid fa-chess-knight"></i>;
      case 'advanced':
        return <i className="fa-solid fa-chess-rook"></i>;
      case 'master':
        return <i className="fa-solid fa-chess-queen"></i>;
      default:
        return <i className="fa-solid fa-chess-board"></i>; // Default icon
    }
  };

  // Format tag display, removing "Openings" when there's a specific opening name
  const formatTagDisplay = (tag) => {
    const tagLabel = TAGS[tag]?.label || tag;
    
    // Check if this is an "Openings" tag with a specific opening name
    if (tagLabel.startsWith('Openings - ')) {
      // Remove "Openings - " prefix and return just the specific opening name
      return tagLabel.replace('Openings - ', '');
    }
    
    return tagLabel;
  };

  return (
    <div
      style={{
        border: '5px solid black',
        borderRadius: '5px',
        background: 'gray',
        overflow: 'auto',
        boxShadow: 'gray',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '140px',
        maxHeight: '140px',
        position: 'relative', // Add position relative for absolute positioning of difficulty icon
      }}
    >
      {!showFullCaption ? (
        // Main view with truncated caption
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Difficulty icon */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '52px',
              width: '40px',
              height: '40px',
              borderRadius: '5px',
              backgroundColor: '#ffd700',
              color: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '24px',
              cursor: 'pointer',
              zIndex: 99,
            }}
            onMouseEnter={() => setShowDifficultyTooltip(true)}
            onMouseLeave={() => setShowDifficultyTooltip(false)}
          >
            {gameMetadata?.difficulty && (
              <span style={{
                
              }}>
                {getDifficultyIcon(gameMetadata.difficulty)}
              </span>
            )}

            {/* Tooltip */}
            {showDifficultyTooltip && (
              <div
                style={{
                  position: 'absolute',
                  top: '',
                  left: difficulty === 'Intermediate' ? '-80px' : '-60px',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 1)',
                  color: 'gold',
                  padding: '12px 10px',
                  borderRadius: '4px',
                  fontSize: '18px',
                  whiteSpace: 'nowrap',
                  zIndex: 1000,
                  fontStyle: 'italic',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.5)',
                }}
              >
                {DIFFICULTY[difficulty]?.label || difficulty}
              </div>
            )}
          </div>

          {/* Player Info Icon - to the right of difficulty icon */}
          {onPlayerInfoClick && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                right: '5px', // Position to the right of the 40px difficulty icon + 10px gap + 8px margin
                width: '40px',
                height: '40px',
                borderRadius: '5px',
                backgroundColor: playerPanelsExpanded ? '#ff0000' : '#ffd700', // Red when expanded to indicate "close"
                color: playerPanelsExpanded ? 'white' : 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '20px',
                cursor: 'pointer',
                zIndex: 99,
                transition: 'background-color 0.2s ease'
              }}
              onClick={onPlayerInfoClick}
              onMouseEnter={(e) => e.target.style.backgroundColor = playerPanelsExpanded ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 215, 0, 0.8)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = playerPanelsExpanded ? '#ff0000' : '#ffd700'}
              title="Toggle Player Info"
            >
              <i className="fas fa-user" />
            </div>
          )}

          {/* Creator Profile Section */}
          <div
            style={{
              padding: '5px 10px',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              background: 'black',
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              height: '50px',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {/* Left side content (rating and username) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexShrink: 0,
                }}
              >
                <Link
                  to={`/profile/${creatorHandle}`}
                  style={{
                    color: 'gold',
                    fontWeight: 'bold',
                    fontSize: '24px',
                  }}
                >
                  {creatorData ? renderUserWithBadge(creatorHandle, creatorData.premium) : renderUserWithBadge(creatorHandle)}
                </Link>
              </div>

              {/* Right side content (tags) - horizontally scrollable */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginLeft: 'auto',
                  overflow: 'auto',
                  whiteSpace: 'nowrap',
                  WebkitOverflowScrolling: 'touch',
                  paddingRight: '90px',
                }}
              >
                {gameMetadata?.tags?.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: TAGS[tag]?.background || colors.tertiary,
                      color: TAGS[tag]?.color || 'black',
                      borderRadius: '4px',
                      padding: '8px',
                      fontSize: '16px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      }
                    }}
                    onClick={() => handleTagClick(tag)}
                  >
                    {formatTagDisplay(tag)}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Caption Section */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgb(26, 38, 53)',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}>
              <div
                style={{
                  fontSize: '22px',
                  color: 'white',
                  width: '90%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                }}
              >
              {cleanProfanity(gameMetadata?.caption) || 'No Title Provided :/'}
              
              </div>
              {gameMetadata?.caption && gameMetadata.caption.length > 40 && (
                <div
                  style={{
                    fontSize: '14px',
                    color: 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    marginTop: '',
                  }}
                  onClick={() => setShowFullCaption(true)}
                >
                  (see more)
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Full caption view
        <div
          style={{
            height: '100%',
            position: 'relative',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            background: 'rgb(26, 38, 53)',
          }}
        >


          {/* X button to close */}
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px', // Moved right to make space for difficulty icon
              fontSize: '24px',
              fontWeight: 'bold',
              cursor: 'pointer',
              zIndex: 10,
              background: 'rgba(0,0,0,0.  5)',
              borderRadius: '50%',
              width: '25px',
              height: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ff0050',
            }}
            onClick={() => setShowFullCaption(false)}
          >
            <i className="fa-regular fa-circle-xmark"></i>
          </div>

          {/* Full caption text */}
          <div
            style={{
              fontSize: '18px',
              color: 'white',
              padding: '15px 15px 15px 20px',
              textAlign: 'left',
              overflowY: 'auto',
              height: '100%',
              display: 'block',
              paddingRight: '40px', // Ensure text doesn't overlap with X button
            }}
          >
            {cleanProfanity(gameMetadata?.caption) || 'No Title Provided :/'}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameTitle;
<style>
  {`
    div::-webkit-scrollbar {
      height: 4px;  /* Horizontal scrollbar height */
      width: 4px;   /* Vertical scrollbar width */
    }

    div::-webkit-scrollbar-track {
      background: rgba(128, 128, 128, 0.1);
      border-radius: 2px;
    }

    div::-webkit-scrollbar-thumb {
      background: rgba(128, 128, 128, 0.4);
      border-radius: 2px;
    }

    div::-webkit-scrollbar-thumb:hover {
      background: rgba(128, 128, 128, 0.5);
    }

    div {
      scrollbar-width: thin;
      scrollbar-color: rgba(128, 128, 128, 0.4) rgba(128, 128, 128, 0.1);
    }
  `}
</style>;

